import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  MarketColumn,
  MarketColumnDetail,
  MarketDashboard,
  MarketGroup,
  MarketWidget,
} from '@clean-code/models';
import { ID } from '@clean-code/models/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MarketDashboardSignalStore } from '../+state/market-dashboard-signal.store';
import { MarketDashboardNormalizerService } from './market-dashboard-normalizer.service';

@Injectable()
export class DashboardStateService {
  private marketDashboardSignalStore = inject(MarketDashboardSignalStore);
  private marketDashboardNormalizer = inject(MarketDashboardNormalizerService);

  private detailColumnEntities$ = toObservable(
    this.marketDashboardSignalStore.detailColumnsEntities,
  );
  private groupEntities$ = toObservable(
    this.marketDashboardSignalStore.groupsEntities,
  );
  private widgetEntities$ = toObservable(
    this.marketDashboardSignalStore.widgetsEntities,
  );

  public editMode$ = new BehaviorSubject<boolean>(false);
  public isLoading$ = new BehaviorSubject<boolean>(false);

  public columns$ = toObservable(
    this.marketDashboardSignalStore.columnsForActiveDashboard,
  );
  public columnCount$ = toObservable(
    this.marketDashboardSignalStore.activeDashboardColumnCount,
  );
  public activeDashboardId$ = toObservable(
    this.marketDashboardSignalStore.activeDashboardId,
  );

  public groups$ = (columnId: ID): Observable<MarketGroup[]> => {
    return this.groupEntities$.pipe(
      map(
        (groups) =>
          groups
            .filter((group) => group.summaryColumnId === columnId)
            .sort(
              (left, right) =>
                (left.settings?.position ?? 0) -
                (right.settings?.position ?? 0),
            ) as MarketGroup[],
      ),
    );
  };
  public detailsGroups$ = (detailColumnId: ID): Observable<MarketGroup[]> => {
    return this.groupEntities$.pipe(
      map(
        (groups) =>
          groups
            .filter((group) => group.detailColumnId === detailColumnId)
            .sort(
              (left, right) =>
                (left.settings?.position ?? 0) -
                (right.settings?.position ?? 0),
            ) as MarketGroup[],
      ),
    );
  };

  public detailsColumns$ = (columnId: ID): Observable<MarketColumnDetail[]> => {
    return this.detailColumnEntities$.pipe(
      map(
        (detailColumns) =>
          detailColumns
            .filter((column) => column.columnId === columnId)
            .sort(
              (left, right) => (left.position ?? 0) - (right.position ?? 0),
            ) as MarketColumnDetail[],
      ),
    );
  };
  public columnDetailsCount$ = (columnId: ID): Observable<number> => {
    return this.detailColumnEntities$.pipe(
      map(
        (detailColumns) =>
          detailColumns.filter((column) => column.columnId === columnId).length,
      ),
    );
  };

  public widgets$ = (groupId: ID): Observable<MarketWidget[]> => {
    return this.widgetEntities$.pipe(
      map((widgets) => widgets.filter((widget) => widget.groupId === groupId)),
    );
  };
  public widgetsCount = (groupId: ID): number => {
    return this.marketDashboardSignalStore
      .widgetsEntities()
      .filter((widget) => widget.groupId === groupId).length;
  };

  public set dashboard(d: MarketDashboard) {
    const normalized = this.marketDashboardNormalizer.normalize(d);

    this.marketDashboardSignalStore.replace(normalized);
  }

  public toggleEditMode() {
    this.editMode$.next(!this.editMode$.value);
  }

  public toggleColumnExpended(value: MarketColumn) {
    this.marketDashboardSignalStore.toggleColumnExpanded(value.id);
  }

  public resetStores() {
    this.marketDashboardSignalStore.reset();
  }
}
